<script>
import useSharedData from "@/use/SharedData.js";
import useValidation from "@/use/Validation.js";
import {computed, onMounted, ref, watch} from "vue";
import StaticList from "@/views/Notification/use/SharedControlProgram.js";
import useTools from "@/use/Tools.js";
import {useControlProgramStore} from "@/views/Notification/stores/ControlProgram.js";
import {useSecurityStore} from "@/stores/Security.js";
import axios from "axios";
import useSharedMethod from "@/use/SharedMethod";

export default {
    name: "ListingModel",
    props: {
        category: String,
    },
    setup(props) {
        const {mxRules} = useValidation();
        const {
            filter,
            total,
            loading,
            tableElevation,
            inputColor,
            backgroundColor,
        } = useSharedData();
        const {$router} = useTools();
        const {transformFilterValues} = useSharedMethod();
        const {controlModules} = StaticList();
        const module = ref('contrat');
        
        const parentPath = computed(() => props.category === 'FILTER' ? 'control_programs' : 'control_issues')
        
        const controlProgramStore = useControlProgramStore();
        const securityStore = useSecurityStore();
        
        function findAll() {
            loading.value = true;
            filter.value.allRecords = true;
            filter.value.module = module.value;
            filter.value.category = props.category;
            // filter.value.sortBy = "colorOrder";
            // filter.value.descending = false;
            axios.get('controlPrograms', {params: transformFilterValues(filter.value)}).then(response => {
                controlProgramStore.setControlPrograms(response.data.elements);
                total.value = response.data.total;
            }).finally(() => {
                loading.value = false;
            })
        }
        
        const children = computed(() => controlProgramStore.controlPrograms.filter(e => e.isMiddle));
        const parents = computed(() => controlProgramStore.controlPrograms.filter(e => !e.isMiddle));
        
        onMounted(() => {
            findAll();
        });
        
        watch(() => module.value, () => {
            findAll();
        });
        
        function view(item, child) {
            $router.push({
                name: `${parentPath.value}.${child ? 'child' : 'parent'}.view`,
                params: {id: item.id, module: module.value}
            });
        }
        
        return {
            parentPath,
            securityStore,
            module,
            mxRules,
            inputColor,
            backgroundColor,
            total,
            loading,
            tableElevation,
            controlModules,
            findAll,
            view,
            children,
            parents,
        }
    },
}
</script>

<template>
    <div>
        <w-app
            :title="category === 'FILTER' ? $t('module.control_program') : $t('module.issue')"
            :total="total"
        >
            <template #rightToolbar>
                <w-btn-reload :loading="loading" @press="findAll"/>
            </template>
            
            <template #content>
                <v-container fluid>
                    <v-row dense>
                        <v-col cols="5">
                            <w-enum-select
                                v-model="module"
                                :items="controlModules"
                                label="Module"
                                not-clearable
                            />
                        </v-col>
                    </v-row>
                </v-container>
                
                <v-row dense>
                    <v-col cols="12" md="6">
                        <v-card flat>
                            <w-toolbar color="blue" :title="$t('common.conditions')">
                                <template #right>
                                    <w-btn-icon
                                        v-if="securityStore.authorities['UPDATE_NOTIFICATION_PARAM']"
                                        icon="mdi-plus-circle-outline"
                                        color="white"
                                        classe="mr-2"
                                        @press="$router.push({name: `${parentPath}.child.add`, params: {module: module}})"
                                    />
                                </template>
                            </w-toolbar>
                            <v-simple-table dense fixed-header height="500">
                                <template v-slot:default>
                                    <thead>
                                    <tr>
                                        <th class="text-left">...</th>
                                        <th class="text-left">
                                            {{ $t('common.label') }}
                                        </th>
                                        <th class="text-left">
                                            {{ $t('common.type') }}
                                        </th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    <tr
                                        v-for="(item, index) in children"
                                        :key="`${index}-child`"
                                        @dblclick="view(item, true)"
                                    >
                                        <td class="text-left">
                                            <v-menu bottom right>
                                                <template v-slot:activator="{ on }">
                                                    <v-btn dark icon color="primary" v-on="on">
                                                        <v-icon>mdi-dots-horizontal</v-icon>
                                                    </v-btn>
                                                </template>
                                                
                                                <v-list nav dense>
                                                    <v-list-item
                                                        v-if="securityStore.authorities['UPDATE_NOTIFICATION_PARAM']"
                                                        @click="$router.push({name: `${parentPath}.child.edit`, params: {id: item.id,module: module,}})"
                                                    >
                                                        <v-list-item-icon>
                                                            <v-icon color="orange">mdi-pencil-box-multiple</v-icon>
                                                        </v-list-item-icon>
                                                        <v-list-item-title>{{ $t("common.edit") }}</v-list-item-title>
                                                    </v-list-item>
                                                    <v-list-item
                                                        v-if="securityStore.authorities['UPDATE_NOTIFICATION_PARAM']"
                                                        @click="$router.push({name: `${parentPath}.child.delete`, params: {id: item.id,module: module}})"
                                                    >
                                                        <v-list-item-icon>
                                                            <v-icon color="red">mdi-trash-can</v-icon>
                                                        </v-list-item-icon>
                                                        <v-list-item-title>{{ $t("common.delete") }}</v-list-item-title>
                                                    </v-list-item>
                                                </v-list>
                                            </v-menu>
                                        </td>
                                        <td>
                                            <w-inline-text bold :text="item.name"/>
                                        </td>
                                        <td>
                                            <w-inline-text :text="item.typeLabel"/>
                                        </td>
                                    </tr>
                                    </tbody>
                                </template>
                            </v-simple-table>
                        </v-card>
                    </v-col>
                    <v-col cols="12" md="6">
                        <v-card flat>
                            <w-toolbar color="teal" :title="$t('common.filters')">
                                <template #right>
                                    <w-btn-icon
                                        v-if="securityStore.authorities['UPDATE_NOTIFICATION_PARAM']"
                                        icon="mdi-plus-circle-outline"
                                        color="white"
                                        classe="mr-2"
                                        @press="$router.push({name: `${parentPath}.parent.add`, params: {module: module}}) "
                                    />
                                </template>
                            </w-toolbar>
                            <v-simple-table dense fixed-header height="500">
                                <template v-slot:default>
                                    <thead>
                                    <tr>
                                        <th class="text-left">...</th>
                                        <th class="text-left">
                                            {{ $t('common.label') }}
                                        </th>
                                        <th class="text-left">
                                            {{ $t('common.message') }}
                                        </th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    <tr
                                        v-for="(item, index) in parents"
                                        :key="`${index}-parent`"
                                        @dblclick="view(item, false)"
                                        :class="item.color"
                                    >
                                        <td class="text-left">
                                            <v-menu bottom right>
                                                <template v-slot:activator="{ on }">
                                                    <v-btn dark icon color="primary" v-on="on">
                                                        <v-icon>mdi-dots-horizontal</v-icon>
                                                    </v-btn>
                                                </template>
                                                
                                                <v-list nav dense>
                                                    <v-list-item
                                                        v-if="securityStore.authorities['UPDATE_NOTIFICATION_PARAM']"
                                                        @click="$router.push({name: `${parentPath}.parent.edit`, params: { id: item.id,module: module }})"
                                                    >
                                                        <v-list-item-icon>
                                                            <v-icon color="orange">mdi-pencil-box-multiple</v-icon>
                                                        </v-list-item-icon>
                                                        <v-list-item-title>{{ $t("common.edit") }}</v-list-item-title>
                                                    </v-list-item>
                                                    <v-list-item
                                                        v-if="securityStore.authorities['UPDATE_NOTIFICATION_PARAM']"
                                                        @click="$router.push({name: `${parentPath}.parent.delete`, params: {id: item.id,module: module}})"
                                                    >
                                                        <v-list-item-icon>
                                                            <v-icon color="red">mdi-trash-can</v-icon>
                                                        </v-list-item-icon>
                                                        <v-list-item-title>{{ $t("common.delete") }}</v-list-item-title>
                                                    </v-list-item>
                                                </v-list>
                                            </v-menu>
                                        </td>
                                        <td>
                                            <w-inline-text bold :text="item.name"/>
                                        </td>
                                        <td>
                                            <w-inline-text :text="item.description"/>
                                        </td>
                                    </tr>
                                    </tbody>
                                </template>
                            </v-simple-table>
                        </v-card>
                    </v-col>
                </v-row>
            </template>
        </w-app>
        <router-view/>
    </div>
</template>

<style scoped>

</style>